import { traceGraph } from './debug.js';
import { reportGraphError } from './errors.js';
import { isExecPortKeyByConvention } from './exec-ports.js';
export class GraphNode {
    id;
    data;
    type;
    outConnections;
    inConnections;
    /** In-port keys that carry execution flow. Reading them as data is never valid. */
    execInPortKeys;
    /** In-port that started the current run, or null when not entered via exec. */
    enteredExecInPortKey = null;
    constructor(id, data, type) {
        this.id = id;
        this.data = data;
        this.type = type;
        this.outConnections = new Map();
        this.inConnections = new Map();
        this.execInPortKeys = new Set(['exec']);
    }
    run() {
        this.getDataFromInPorts();
    }
    /**
     * Releases everything this node subscribed to. Called when the graph instance the
     * node belongs to is discarded - most notably a function body that a new call to
     * the same call node replaces. Nodes without subscriptions need nothing.
     */
    dispose() { }
    /**
     * Invalidates cached outputs downstream of a write. Only nodes that cache (or
     * forward a cache invalidation, like a Reroute) have anything to do here.
     */
    markDirty() { }
    /**
     * True when this node drives execution through the given out port rather than
     * data. Consumers use it to tell exec wires from value wires.
     */
    isExecOutPort(outPortKey) {
        return isExecPortKeyByConvention(outPortKey);
    }
    /** Called by the upstream node right before it runs this node over an exec wire. */
    enterExecInPort(inPortKey) {
        this.execInPortKeys.add(inPortKey);
        this.enteredExecInPortKey = inPortKey;
    }
    getOutPortData(outPortKey) {
        const data = this.data[outPortKey];
        if (data === undefined) {
            console.error(`Node ${this.id} has no data for out port ${outPortKey}`);
            return undefined;
        }
        return data;
    }
    getDataFromInPorts() {
        for (const [inPortKey, inConnections] of this.inConnections) {
            if (this.execInPortKeys.has(inPortKey))
                continue;
            const inConnection = inConnections[0];
            if (!inConnection)
                continue;
            if (this.isFedByExecWire(inPortKey, inConnection))
                continue;
            this.data[inPortKey] = inConnection.connectedNode.getOutPortData(inConnection.outPortKey);
        }
    }
    /**
     * A node can have exec in-ports beyond `exec` (a Function Return mirrors every
     * exec output of its interface). Until such a port has fired once, only the
     * upstream side knows it is an exec wire — reading it as data would just
     * produce a bogus "no data for out port" error.
     */
    isFedByExecWire(inPortKey, inConnection) {
        if (!inConnection.connectedNode.isExecOutPort(inConnection.outPortKey))
            return false;
        this.execInPortKeys.add(inPortKey);
        return true;
    }
    /**
     * An upstream value arrived after this node had already read it. Nodes that pull
     * their inputs on every run need nothing here; nodes that snapshot or cache them
     * override this.
     */
    refreshAfterUpstreamValueChange() { }
    /**
     * Announces out-port values that were written outside a pull - a function Return
     * fires long after the graph was instantiated. Event nodes snapshot their inputs
     * when they subscribe, so without this they keep matching against the value they
     * saw while the graph was still booting.
     */
    notifyValueConsumersOfChangedOutputs(changedOutPortKeys) {
        const pendingConsumers = changedOutPortKeys.flatMap((portKey) => this.readValueConsumers(portKey));
        const notified = new Set([this]);
        while (pendingConsumers.length > 0) {
            const consumer = pendingConsumers.shift();
            if (notified.has(consumer))
                continue;
            notified.add(consumer);
            consumer.refreshAfterUpstreamValueChange();
            pendingConsumers.push(...consumer.readAllValueConsumers());
        }
    }
    /** Nodes wired to an out port over a value wire - exec wires carry no data. */
    readValueConsumers(outPortKey) {
        if (this.isExecOutPort(outPortKey))
            return [];
        const outConnections = this.outConnections.get(outPortKey) ?? [];
        return outConnections.map((outConnection) => outConnection.connectedNode);
    }
    readAllValueConsumers() {
        return [...this.outConnections.keys()].flatMap((portKey) => this.readValueConsumers(portKey));
    }
    triggerOutPort(portKey) {
        void this.runOutPort(portKey);
    }
    /** Runs all nodes connected to an exec output and waits until they have entered. */
    async runOutPort(portKey) {
        const outConnections = this.outConnections.get(portKey) || [];
        if (outConnections.length === 0)
            return;
        traceGraph(`${this.type} (${this.id}) -> "${portKey}" -> ${outConnections
            .map((connection) => `${connection.connectedNode.type}(${connection.connectedNode.id})`)
            .join(', ')}`);
        const executions = outConnections.map((outConnection) => {
            outConnection.connectedNode.enterExecInPort(outConnection.inPortKey);
            return this.runConnectedNode(outConnection.connectedNode);
        });
        await Promise.all(executions);
    }
    /**
     * One throwing node must not cancel its siblings on the same out port, and its
     * failure must be reported instead of vanishing as an unhandled rejection.
     */
    async runConnectedNode(node) {
        const onFailure = (error) => reportGraphError({
            kind: 'exec',
            nodeType: node.type,
            nodeId: node.id,
            summary: `threw while running - triggered by ${this.type} (${this.id})`,
            error,
        });
        try {
            await Promise.resolve(node.run());
        }
        catch (error) {
            onFailure(error);
        }
    }
}
